/**
 * SimulcastPanel — the other places a service goes out, and whether it is kept.
 *
 * One broadcast, several platforms: the stream key for each is typed in once
 * here and never comes back out — the panel can say a key is set, not what it
 * is. A destination added while the service is already on air is attached to
 * the running broadcast, so nobody has to stop and start it.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Share2, Trash2, Loader2, Plus, CircleDot, Info } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = Array.isArray(err.detail)
      ? err.detail.map((d) => d.msg || String(d)).join('; ')
      : err.detail;
    throw new Error(detail || `Request failed (${res.status})`);
  }
  return res.json();
};

export const SimulcastPanel = ({ scope, ident, dark = false }) => {
  const qc = useQueryClient();
  const [platform, setPlatform] = useState('youtube');
  const [streamKey, setStreamKey] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [watchUrl, setWatchUrl] = useState('');
  const [adding, setAdding] = useState(false);

  const key = ['live-destinations', scope, ident];
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api(`/live/${scope}/${ident}/destinations`),
    retry: false,
  });

  const destinations = data?.destinations || [];
  const platforms = data?.platforms || [];
  const chosen = platforms.find((p) => p.id === platform);

  const add = useMutation({
    mutationFn: () => api(`/live/${scope}/${ident}/destinations`, {
      method: 'POST',
      body: JSON.stringify({ platform, stream_key: streamKey.trim(), server_url: serverUrl.trim(), watch_url: watchUrl.trim() }),
    }),
    onSuccess: (res) => {
      toast.success(res.attached_live
        ? `${res.label} added — it is joining the broadcast now`
        : `${res.label} added`);
      setStreamKey(''); setServerUrl(''); setWatchUrl(''); setAdding(false);
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id) => api(`/live/${scope}/${ident}/destinations/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => toast.error(e.message),
  });

  const text = dark ? 'text-slate-100' : 'text-slate-900';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const field = dark ? 'bg-slate-950 border-slate-700 text-slate-100' : '';

  if (isLoading) {
    return (
      <div className={`py-6 text-center text-sm ${muted}`} data-testid="simulcast-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading destinations…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="simulcast-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={`text-sm font-medium flex items-center gap-2 ${text}`}>
            <Share2 className="h-4 w-4" /> Also going out on
          </p>
          <p className={`text-xs ${muted}`}>
            The same service, pushed to each platform at once. Add the stream key each one gives you.
          </p>
        </div>
        {!adding && (
          <Button size="sm" variant="outline"
                  className={dark ? 'border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800' : ''}
                  onClick={() => setAdding(true)} data-testid="add-destination-btn">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        )}
      </div>

      {destinations.length === 0 && !adding && (
        <p className={`text-xs ${muted}`} data-testid="no-destinations">
          Nowhere else yet — this service only goes out on the church page.
        </p>
      )}

      <div className="space-y-1.5">
        {destinations.map((d) => (
          <div key={d.id}
               className={`flex items-center justify-between gap-2 rounded-md px-3 py-2 ${dark ? 'bg-slate-950' : 'bg-slate-50'}`}
               data-testid={`destination-row-${d.id}`}>
            <div className="min-w-0">
              <p className={`text-sm truncate ${text}`}>{d.label}</p>
              <p className={`text-[11px] ${muted}`}>
                {d.key_set ? 'Stream key saved' : 'No key'}
                {d.last_error ? ` · ${d.last_error}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {d.enabled && <Badge variant="outline" className="text-[10px]">on</Badge>}
              <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500"
                      onClick={() => remove.mutate(d.id)}
                      data-testid={`remove-destination-${d.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {adding && (
        <div className={`space-y-2 rounded-md p-3 ${dark ? 'bg-slate-950' : 'bg-slate-50'}`}
             data-testid="add-destination-form">
          <div>
            <Label className={dark ? 'text-slate-300' : ''}>Platform</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className={`mt-1 ${field}`} data-testid="destination-platform-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {platforms.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {chosen?.needs_server_url && (
            <div>
              <Label className={dark ? 'text-slate-300' : ''}>Server URL</Label>
              <Input value={serverUrl} onChange={(e) => setServerUrl(e.target.value)}
                     placeholder="rtmp://… (the address that platform shows you)"
                     className={`mt-1 ${field}`} data-testid="destination-server-input" />
            </div>
          )}
          <div>
            <Label className={dark ? 'text-slate-300' : ''}>Stream key</Label>
            <Input value={streamKey} onChange={(e) => setStreamKey(e.target.value)}
                   placeholder="Paste the stream key"
                   autoComplete="off" spellCheck={false}
                   className={`mt-1 ${field}`} data-testid="destination-key-input" />
            <p className={`text-[11px] mt-1 ${muted}`}>
              Treated as a password: stored encrypted and never shown again.
            </p>
          </div>
          <div>
            <Label className={dark ? 'text-slate-300' : ''}>Where it can be watched back (optional)</Label>
            <Input value={watchUrl} onChange={(e) => setWatchUrl(e.target.value)}
                   placeholder="https://youtube.com/… the page this channel keeps broadcasts on"
                   className={`mt-1 ${field}`} data-testid="destination-watch-input" />
            <p className={`text-[11px] mt-1 ${muted}`}>
              This platform keeps its own copy of the broadcast. Give the page it
              keeps it on and a lesson can be replayed from there — a copy that
              does not depend on the lecturer's laptop surviving.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => add.mutate()} disabled={add.isPending}
                    className={dark ? 'bg-indigo-600 hover:bg-indigo-500 text-white' : ''}
                    data-testid="save-destination-btn">
              {add.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save
            </Button>
            <Button size="sm" variant={dark ? 'outline' : 'ghost'}
                    className={dark ? 'border-slate-600 text-slate-200 hover:bg-slate-800' : ''}
                    onClick={() => { setAdding(false); setStreamKey(''); setServerUrl(''); setWatchUrl(''); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className={`text-xs flex items-start gap-1.5 pt-1 ${muted}`} data-testid="recording-state">
        {data?.recording ? (
          <>
            <CircleDot className="h-3.5 w-3.5 mt-0.5 text-rose-500" />
            Services are recorded automatically from the moment they start.
          </>
        ) : (
          <>
            <Info className="h-3.5 w-3.5 mt-0.5" />
            {data?.recording_blocked_reason || 'Recording is not set up yet.'}
          </>
        )}
      </div>
    </div>
  );
};

export default SimulcastPanel;
